import { createCognitoClient } from './cognito';
import type { SignInResult } from './cognito';

// Login próprio; mantém o callback PKCE para fluxos antigos já iniciados.

const CFG = {
  domain: import.meta.env.VITE_COGNITO_DOMAIN ?? 'pixeldmp-user-pool-domain',
  region: import.meta.env.VITE_COGNITO_REGION ?? 'sa-east-1',
  clientId: import.meta.env.VITE_COGNITO_CLIENT_ID ?? '5anjrtqi3ukskauel51a1m5e3l',
  scope: import.meta.env.VITE_COGNITO_SCOPE ?? 'openid',
};

const HOSTED_UI = `https://${CFG.domain}.auth.${CFG.region}.amazoncognito.com`;
const REDIRECT_URI = () => `${window.location.origin}/auth/callback`;
export const cognito = createCognitoClient(CFG);

// Token em localStorage: sessionStorage é por aba, e abrir um link em nova aba
// caía na tela de login. PKCE verifier/state/return seguem em sessionStorage
// (o fluxo do Hosted UI acontece na mesma aba).
const TOKEN_KEY = 'pdmp.id_token';
const EXP_KEY = 'pdmp.id_exp';
const VERIFIER_KEY = 'pdmp.pkce_verifier';
const STATE_KEY = 'pdmp.oauth_state';
const RETURN_KEY = 'pdmp.return_to';
const ACCESS_KEY = 'pdmp.access_token';
const SOURCE_KEY = 'pdmp.auth_source';

// ---------- token storage ----------
interface JwtPayload {
  exp?: number;
  email?: string;
  'cognito:groups'?: string[];
  'custom:admin'?: string;
  [k: string]: unknown;
}
function decodeJwt(token: string): JwtPayload {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return {};
  }
}

export function getIdToken(): string | null {
  // Fallback no sessionStorage cobre sessões abertas antes desta mudança.
  const token = localStorage.getItem(TOKEN_KEY) ?? sessionStorage.getItem(TOKEN_KEY);
  const exp = Number(localStorage.getItem(EXP_KEY) || sessionStorage.getItem(EXP_KEY) || 0);
  if (!token) return null;
  if (exp && Date.now() / 1000 > exp) {
    clearSession();
    return null;
  }
  return token;
}

export function isAuthenticated(): boolean {
  return getIdToken() !== null;
}

export function currentUser(): { email?: string; isAdmin: boolean; impersonated: boolean } | null {
  const token = getIdToken();
  if (!token) return null;
  const p = decodeJwt(token);
  const groups = p['cognito:groups'] || [];
  return {
    email: p.email,
    isAdmin: groups.includes('admin') || groups.includes('Admin') || p['custom:admin'] === '1',
    impersonated: p['impersonated'] === true,
  };
}

/** Substitui a sessão pelo token informado (usado pelo impersonation:
 *  admin "loga como" um usuário de cliente com o token emitido pelo backend). */
export function adoptToken(idToken: string): void {
  const p = decodeJwt(idToken);
  if (typeof p.exp !== 'number' || !Number.isFinite(p.exp) || p.exp <= Date.now() / 1000) {
    throw new Error('Invalid session. Please sign in again.');
  }
  clearSession();
  localStorage.setItem(TOKEN_KEY, idToken);
  localStorage.setItem(EXP_KEY, String(p.exp));
  localStorage.setItem(SOURCE_KEY, 'direct');
}

export function completeSignIn(result: Extract<SignInResult, { type: 'signedIn' }>): void {
  adoptToken(result.idToken);
  localStorage.setItem(ACCESS_KEY, result.accessToken);
}

function clearSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(EXP_KEY);
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(EXP_KEY);
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(SOURCE_KEY);
}

/** Caminho salvo antes do redirect ao Hosted UI — volta para onde o usuário ia. */
export function consumeReturnPath(): string | null {
  const p = sessionStorage.getItem(RETURN_KEY);
  sessionStorage.removeItem(RETURN_KEY);
  return p && p.startsWith('/') && !p.startsWith('//') && !p.startsWith('/auth/') ? p : null;
}

/** Troca o code por tokens no callback. Retorna true em sucesso. */
export async function handleCallback(search: string): Promise<boolean> {
  const q = new URLSearchParams(search);
  const code = q.get('code');
  const state = q.get('state');
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  const savedState = sessionStorage.getItem(STATE_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);

  if (!code || !verifier || !state || state !== savedState) return false;

  const res = await fetch(`${HOSTED_UI}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: CFG.clientId,
      code,
      redirect_uri: REDIRECT_URI(),
      code_verifier: verifier,
    }),
  });
  if (!res.ok) return false;

  const tokens = (await res.json()) as { id_token?: string };
  if (!tokens.id_token) return false;

  adoptToken(tokens.id_token);
  localStorage.setItem(SOURCE_KEY, 'hosted');
  return true;
}

export function logout(): void {
  const source = localStorage.getItem(SOURCE_KEY);
  const accessToken = localStorage.getItem(ACCESS_KEY);
  clearSession();
  if (source === 'direct') {
    if (accessToken) void cognito.signOut(accessToken).catch(() => { /* local session is already cleared */ });
    window.location.assign('/');
    return;
  }
  const params = new URLSearchParams({
    client_id: CFG.clientId,
    logout_uri: `${window.location.origin}/`,
  });
  window.location.assign(`${HOSTED_UI}/logout?${params}`);
}
